import { scoreFill } from './NoteMapGrid';
import React, { useRef, useState, useLayoutEffect, useMemo, useEffect } from 'react';
import { MindMapNode, Language, NodeAttachment, NodeWidth, NodeType } from '../types';
import { Check, Upload, X, Brain, Loader2, GitBranchPlus, Trash2, GripVertical, Link as LinkIcon, ArrowUpRight, Unplug, Sparkles, ChevronDown, ChevronUp, Maximize2, Minimize2, Wand2, Mic, TextQuote, ImagePlus, Bold, Italic, List, CheckCircle2, HelpCircle, Copy, LifeBuoy, Unlink, CornerUpLeft, MoreHorizontal, Image as ImageIcon, Eye, MoreVertical, Hash, Plus, Tag, Zap } from 'lucide-react';
import { transcribeAndRefineAudio, formatNoteContent, extractImageHashtags } from '../services/geminiService';
import { insertMarkdown, parseMarkdown, applyKatexToElement } from '../utils/markdownUtils';
import { DetailedFeedbackModal } from './DetailedFeedbackModal';
import { ImageGalleryModal } from './ImageGalleryModal';
import { NodeTypeModal } from './NodeTypeModal';

const MAX_TEXT_HEIGHT = 320;

const RenderedFeedback = ({ text, className }: { text: string; className?: string }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.innerHTML = parseMarkdown(text);
        applyKatexToElement(el);
        // @ts-ignore
        if (!window.katex) {
            const t = setTimeout(() => { el.innerHTML = parseMarkdown(text); applyKatexToElement(el); }, 800);
            return () => clearTimeout(t);
        }
    }, [text]);
    return <div ref={ref} className={className} />;
};

const Tip: React.FC<{ text: string; children: React.ReactNode; side?: 'top' | 'bottom'; align?: 'center' | 'right' }> = ({ text, children, side = 'top', align = 'center' }) => (
  <div className="relative group/tip">
    {children}
    <div className={`pointer-events-none absolute ${side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} ${align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'} px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-[200] shadow-lg w-max max-w-[200px] text-center`}>
      {text}
      <span className={`absolute ${side === 'top' ? 'top-full border-t-gray-900 dark:border-t-gray-700' : 'bottom-full border-b-gray-900 dark:border-b-gray-700'} ${align === 'right' ? 'right-3' : 'left-1/2 -translate-x-1/2'} border-4 border-transparent`} />
    </div>
  </div>
);

interface NodeCardProps {
  node: MindMapNode;
  isSelected: boolean;
  onUpdate: (id: string, data: Partial<MindMapNode>) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string, e: React.MouseEvent | React.PointerEvent) => void;
  onClick?: (id: string) => void;
  onStartDrag: (id: string, e: React.PointerEvent) => void;
  onAnalyze: (id: string, mode?: 'simple' | 'detailed') => void;
  onGetFramework: (id: string) => void;
  onGenerateTitle: (id: string) => Promise<string>;
  onAddChild: (parentId: string) => void;
  onMagicExpand: (parentId: string) => void;
  onStartLink: (id: string) => void;
  onUnlink: (targetId: string) => void;
  onUpdateLink?: (linkId: string, color: string) => void;
  onJumpToNode: (id: string) => void;
  onExpandNote: (id: string) => void;
  linkedNodes?: { id: string; title: string; linkId: string; linkColor?: string; relationType?: 'parent' | 'child' | 'peer' }[];
  isLinkingMode: boolean;
  isLinkSource: boolean;
  scale: number;
  /** Canvas score map: cover each card with its video-potential score in the colours of YouTube Manager's map tab. */
  scoreMap?: boolean;
  language: Language;
  layoutMode?: 'map' | 'card' | 'stream' | 'board' | 'blog';
  parentTitle?: string;
  isReviewMode?: boolean;
  isDropCandidate?: boolean;
  isMagicExpanding?: boolean;
  isQuizMode?: boolean;
  isQuizSelected?: boolean;
  onQuizSelect?: (id: string) => void;
  isGuideMode?: boolean;
  isGuideSelected?: boolean;
  onGuideSelect?: (id: string) => void;
  nodeWidth?: NodeWidth;
  searchQuery?: string;
  readOnly?: boolean;
  onStartEditing?: () => void;
  onEndEditing?: () => void;
  onNodeTypeChange?: (nodeId: string, newType: NodeType) => void;
  lodLevel?: 'full' | 'skeleton' | 'mini' | 'dot';
  onStartReview?: (nodeId: string) => void; // NEW: Trigger App-level Review
  onMoveNode?: (nodeId: string, direction: 'up' | 'down') => void; // NEW: Reorder siblings
  onTagSearch?: (tag: string) => void; // NEW: Click tag to search
  onGenerateReviewQuestions?: (nodeId: string, origin: string) => void;
  onViewNoteQuestions?: (nodeId: string) => void;
  lastSavedTime?: Date | null;
  syncState?: 'saved' | 'saving' | 'error';
  globalExpandCycle?: 0 | 1 | 2 | 3 | 4;
  onExpandCycleChange?: (nodeId: string, cycle: 0 | 1 | 2) => void;
  isSourceRoot?: boolean;
  hasChildNodes?: boolean;
  isMobile?: boolean;
  onMobileImageOpen?: (src: string) => void;
  isZenMode?: boolean;
  isSurveyMode?: boolean;
  isLinkHovered?: boolean;
  onSurveyHover?: (node: MindMapNode, rect: DOMRect) => void;
  onSurveyLeave?: () => void;
  onNodeContextMenu?: (e: React.MouseEvent, nodeId: string) => void;
  isIndexed?: boolean;
}

const LABELS = {
  english: {
    titlePlaceholder: "Topic Title...",
    addImage: "Add Image",
    addMoreImages: "Add More",
    placeholder: "Explain this concept...",
    score: "Score",
    addBranch: "Branch",
    linkNode: "Link",
    cancelLink: "Cancel",
    dropImage: "Drop Image",
    statusMissing: "Missing Explanation",
    statusMastered: "Mastered",
    statusReview: "Needs Review",
    statusUnverified: "Unchecked",
    related: "Related",
    magicTitle: "Auto-Title",
    attachTo: "Attach",
    magicExpand: "Expand",
    recording: "Listening...",
    expand: "Focus",
    format: "Format",
    studyFramework: "Framework",
    getFramework: "Guide",
    unlink: "Unlink",
    replyTo: "From",
    detailedCheck: "Deep Dive",
    viewDetails: "View Details",
    strengths: "Strengths",
    improvements: "To Improve",
    bonus: "Bonus Points"
  },
  vietnamese: {
    titlePlaceholder: "Tiêu đề...",
    addImage: "Thêm ảnh",
    addMoreImages: "Thêm ảnh",
    placeholder: "Giải thích...",
    score: "Điểm",
    addBranch: "Nhánh",
    linkNode: "Liên kết",
    cancelLink: "Hủy",
    dropImage: "Thả ảnh",
    statusMissing: "Thiếu nội dung",
    statusMastered: "Đã hiểu",
    statusReview: "Xem lại",
    statusUnverified: "Chưa chấm",
    related: "Liên quan",
    magicTitle: "Tự đặt tên",
    attachTo: "Gắn",
    magicExpand: "Mở rộng",
    recording: "Đang nghe...",
    expand: "Tập trung",
    format: "Định dạng",
    studyFramework: "Khung bài",
    getFramework: "Gợi ý",
    unlink: "Hủy",
    replyTo: "Từ ý",
    detailedCheck: "Phân tích sâu",
    viewDetails: "Xem chi tiết",
    strengths: "Điểm tốt",
    improvements: "Cần cải thiện",
    bonus: "Điểm thưởng"
  }
};

// The result is injected as HTML, and titles can come from someone else's public notebook — so the
// title is HTML-escaped first (a title like <img onerror=…> must render as text, never run).
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const highlightText = (text: string, query: string): string => {
  if (!text) return '';
  const safeText = escapeHtml(text);
  if (!query) return safeText;
  const safeQuery = escapeHtml(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return safeText.replace(new RegExp(`(${safeQuery})`, 'gi'), '<mark class="bg-yellow-300 text-black rounded-sm px-0.5">$1</mark>');
};

const NodeCardComponent: React.FC<NodeCardProps> = ({
  node,
  isSelected,
  onUpdate,
  onDelete,
  onSelect,
  onClick,
  onStartDrag,
  onAnalyze,
  onGetFramework,
  onGenerateTitle,
  onAddChild,
  onMagicExpand,
  onStartLink,
  onUnlink,
  onUpdateLink,
  onJumpToNode,
  onExpandNote,
  linkedNodes = [],
  isLinkingMode,
  isLinkSource,
  scale,
  scoreMap = false,
  language,
  layoutMode = 'map',
  parentTitle,
  isReviewMode = false,
  isDropCandidate = false,
  isMagicExpanding = false,
  isQuizMode = false,
  isQuizSelected = false,
  onQuizSelect,
  isGuideMode = false,
  isGuideSelected = false,
  onGuideSelect,
  nodeWidth = 'standard',
  searchQuery = "",
  readOnly = false,
  onStartEditing,
  onEndEditing,
  onNodeTypeChange,
  lodLevel = 'full',
  onStartReview, // NEW
  onMoveNode, // NEW
  onTagSearch, // NEW
  onGenerateReviewQuestions,
  onViewNoteQuestions,
  lastSavedTime,
  syncState,
  globalExpandCycle,
  onExpandCycleChange,
  isSourceRoot = false,
  hasChildNodes = false,
  isMobile = false,
  onMobileImageOpen,
  isZenMode = false,
  isSurveyMode = false,
  isLinkHovered = false,
  onSurveyHover,
  onSurveyLeave,
  onNodeContextMenu,
  isIndexed = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const startClickPosRef = useRef<{ x: number; y: number } | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const [isDragActive, setIsDragActive] = useState(false);
  const [isTitleLoading, setIsTitleLoading] = useState(false);
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(true);
  const isContentExpanded = node.isExpanded !== false;
  const [expandCycle, setExpandCycle] = useState<0 | 1 | 2>(0);
  const displayExpanded = expandCycle >= 1 || isContentExpanded;

  useEffect(() => {
    if (globalExpandCycle === undefined) return;
    // global 3 = text open, images compact → same as local state 1
    setExpandCycle(globalExpandCycle === 3 ? 1 : globalExpandCycle as 0 | 1 | 2);
  }, [globalExpandCycle]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [imageGallery, setImageGallery] = useState<{ isOpen: boolean; initialIndex: number } | null>(null);
  const [isNodeTypeModalOpen, setIsNodeTypeModalOpen] = useState(false);
  const [isMobileScoreExpanded, setIsMobileScoreExpanded] = useState(false);
  const [boldMode, setBoldMode] = useState(false);
  const [isExtractingTags, setIsExtractingTags] = useState(false);
  const [addTagInput, setAddTagInput] = useState<string | null>(null);
  const addTagInputRef = useRef<HTMLInputElement>(null);

  // Local content state for smooth typing (prevent cursor jump during autosave)
  const [localContent, setLocalContent] = useState(node.content);
  const debouncedUpdateRef = useRef<NodeJS.Timeout | null>(null);

  // Check if this is a label node (title-only)
  const isLabel = node.nodeType === 'label';

  // Local state for title to prevent parent re-renders on every keystroke
  const [localTitle, setLocalTitle] = useState(node.title);
  useEffect(() => {
    setLocalTitle(node.title);
  }, [node.title]);

  // Sync local content ONLY when switching nodes, NOT during autosave
  useEffect(() => {
    setLocalContent(node.content);
    // Cancel pending updates when switching nodes
    if (debouncedUpdateRef.current) {
      clearTimeout(debouncedUpdateRef.current);
      debouncedUpdateRef.current = null;
    }
  }, [node.id]);

  // Flush debounced updates on unmount
  useEffect(() => {
    return () => {
      if (debouncedUpdateRef.current) {
        clearTimeout(debouncedUpdateRef.current);
        onUpdate(node.id, { content: localContent });
      }
    };
  }, [localContent, node.id, onUpdate]);

  const labels = LABELS[language];
  const isMap = layoutMode === 'map';
  const isStream = layoutMode === 'stream';
  // Stream View on mobile is a "quick scan" surface — keep only title, image, a preview of
  // text/AI feedback, the Open button, cross-link chips, and delete. Everything else (tags,
  // framework box, AI Check/Recall/Guide actions, saved-time row, review-question CTA) is
  // chrome that belongs in the full modal, reached via the Open button.
  const compactStream = isStream && !!isMobile;
  // pb-8 reserves room for the floating "open to edit" button, which only renders
  // on !isMobile (see the non-map expand button below) or in map mode (its own expand pill).
  // On mobile non-map (stream/card view) nothing occupies that space, so don't pad for it.
  const bottomPad = isMap ? 'pb-8' : isMobile ? 'pb-3' : 'pb-8';

  const { titleMatch, contentMatch, tagMatch } = useMemo(() => {
    if (!searchQuery) return { titleMatch: false, contentMatch: false, tagMatch: false };
    const q = searchQuery.toLowerCase();
    return {
      titleMatch: (node.title ?? '').toLowerCase().includes(q),
      contentMatch: (node.content ?? '').toLowerCase().includes(q),
      tagMatch: (node.tags || []).some(tag => tag.toLowerCase().includes(q))
    };
  }, [searchQuery, node.title, node.content, node.tags]);

  const handleContentInteraction = (e: React.BaseSyntheticEvent) => {
    if (isLinkingMode || isQuizMode) return;
    onSelect(node.id, e as any);
    e.stopPropagation();
  };

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea && isContentExpanded && isEditing) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      if (isStream) {
        textarea.style.height = `${Math.max(80, scrollHeight)}px`;
        textarea.style.overflowY = 'hidden';
      } else {
        const newHeight = Math.min(Math.max(80, scrollHeight), MAX_TEXT_HEIGHT);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = scrollHeight > MAX_TEXT_HEIGHT ? 'auto' : 'hidden';
      }
    }
  };

  useLayoutEffect(() => {
    if (isContentExpanded && isEditing) {
      requestAnimationFrame(() => adjustHeight());
    }
  }, [localContent, isContentExpanded, isEditing]);

  const triggerExtractTags = async (images: { base64: string; mimeType: string }[]) => {
    if (readOnly || isExtractingTags || !images.length) return;
    setIsExtractingTags(true);
    try {
      const result = await extractImageHashtags(images, language);
      if (result.tags.length > 0) {
        const existingTags = node.tags || [];
        const merged = Array.from(new Set([...existingTags, ...result.tags]));
        onUpdate(node.id, { tags: merged });
      }
    } finally {
      setIsExtractingTags(false);
    }
  };

  const processFile = (file: File) => {
    if (readOnly) return;
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        if (node.imageBase64) {
          const newAttachment: NodeAttachment = {
            id: Math.random().toString(36).substring(7),
            type: 'image',
            content: base64,
            mimeType: file.type,
            createdAt: Date.now()
          };
          const updatedAttachments = [...(node.attachments || []), newAttachment];
          onUpdate(node.id, { attachments: updatedAttachments });
        } else {
          onUpdate(node.id, { imageBase64: base64, imageMimeType: file.type });
        }
        // Trigger auto-hashtag extraction (non-blocking)
        triggerExtractTags([{ base64, mimeType: file.type }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const removeAttachment = (attId: string) => {
    if (readOnly) return;
    const newAttachments = (node.attachments || []).filter(a => a.id !== attId);
    onUpdate(node.id, { attachments: newAttachments });
  };

  const removeMainImage = () => {
    if (readOnly) return;
    onUpdate(node.id, { imageBase64: null, imageMimeType: null });
  };


  const allImages: { id: string; type: 'image' | 'drawing'; content: string; src: string; isMain: boolean; mimeType: string }[] = [];
  if (node.imageBase64) allImages.push({ id: 'main', type: 'image', content: node.imageBase64, src: node.imageBase64, isMain: true, mimeType: node.imageMimeType || 'image/jpeg' });
  if (node.drawingBase64) allImages.push({ id: 'drawing', type: 'drawing', content: node.drawingBase64, src: node.drawingBase64, isMain: false, mimeType: 'image/png' });
  // Include new multi-drawings from attachments
  if (node.attachments) {
    node.attachments.forEach(att => {
      if (!att) return;
      allImages.push({ ...att, src: att.content, isMain: false });
    });
  }
  const hasImages = allImages.length > 0;

  const handleMagicTitle = async () => {
    if (readOnly || isTitleLoading) return;
    if (!node.content && !node.imageBase64) return;
    if (node.title?.trim()) {
      const confirmMsg = language === 'vietnamese'
        ? 'Note này đã có tiêu đề. Bạn có muốn tạo tiêu đề mới thay thế không?'
        : 'This note already has a title. Replace it with an AI-generated one?';
      if (!window.confirm(confirmMsg)) return;
    }
    setIsTitleLoading(true);
    try {
      const newTitle = await onGenerateTitle(node.id);
      if (newTitle) onUpdate(node.id, { title: newTitle });
    } catch (error) { console.error("Failed to generate title", error); } finally { setIsTitleLoading(false); }
  };



  const handleInsertMarkdown = (prefix: string, suffix: string) => {
    if (readOnly || !textareaRef.current) return;
    const newText = insertMarkdown(textareaRef.current, prefix, suffix);
    if (newText !== null) {
      setLocalContent(newText);
      onUpdate(node.id, { content: newText });
      textareaRef.current.focus();
    }
  };

  const handleBoldToggle = () => {
    if (readOnly) return;
    const ta = textareaRef.current;
    if (!ta) { handleInsertMarkdown('**', '**'); return; }
    const hasSelection = ta.selectionStart !== ta.selectionEnd;
    if (hasSelection) { handleInsertMarkdown('**', '**'); return; }
    const text = ta.value;
    const pos = ta.selectionStart;
    const newText = text.substring(0, pos) + '**' + text.substring(pos);
    const newCursorPos = pos + 2;
    if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
    setLocalContent(newText);
    onUpdate(node.id, { content: newText });
    setBoldMode(prev => !prev);
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(newCursorPos, newCursorPos); });
  };

  const handleTextareaBlur = () => {
    if (boldMode && textareaRef.current) {
      const ta = textareaRef.current;
      const pos = ta.selectionStart;
      const text = ta.value;
      const newText = text.substring(0, pos) + '**' + text.substring(pos);
      if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
      setLocalContent(newText);
      onUpdate(node.id, { content: newText });
      setBoldMode(false);
    }
    setIsEditing(false);
    onEndEditing?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (readOnly) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') { e.preventDefault(); handleBoldToggle(); }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') { e.preventDefault(); handleInsertMarkdown('_', '_'); }
  };

  const handleVoiceRecording = async () => {
    if (readOnly) return;
    if (isRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];
        mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) audioChunksRef.current.push(event.data); };
        mediaRecorder.onstop = async () => {
          setIsTranscribing(true);
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const reader = new FileReader();
          reader.readAsDataURL(audioBlob);
          reader.onloadend = async () => {
            const base64String = reader.result as string;
            const cleanBase64 = base64String.split(',')[1];
            try {
              const refinedText = await transcribeAndRefineAudio(cleanBase64, language);
              if (refinedText) {
                const newContent = localContent ? `${localContent}\n\n${refinedText}` : refinedText;
                setLocalContent(newContent);
                onUpdate(node.id, { content: newContent });
              }
            } catch (err) { console.error("Transcription failed", err); } finally { setIsTranscribing(false); }
          };
          stream.getTracks().forEach(track => track.stop());
        };
        mediaRecorder.start();
        setIsRecording(true);
      } catch (err) { alert('Cannot access microphone.'); }
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    const types = Array.from(e.dataTransfer.types);
    if (types.includes('Files') || types.includes('application/x-studyboard-image')) setIsDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragActive(false);
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); if (!isDragActive) setIsDragActive(true); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragActive(false);
    if (readOnly) return;
    const imagePayload = e.dataTransfer.getData('application/x-studyboard-image');
    if (imagePayload) {
      try {
        const { content, mimeType } = JSON.parse(imagePayload);
        const newAtt: NodeAttachment = { id: `img_${Date.now()}`, type: 'image', content, mimeType, createdAt: Date.now() };
        if (node.imageBase64) {
          const mainAtt: NodeAttachment = { id: `img_main_${Date.now() - 1}`, type: 'image', content: node.imageBase64, mimeType: node.imageMimeType || 'image/jpeg', createdAt: Date.now() - 1 };
          onUpdate(node.id, { imageBase64: null, imageMimeType: null, attachments: [...(node.attachments || []), mainAtt, newAtt] });
        } else {
          onUpdate(node.id, { attachments: [...(node.attachments || []), newAtt] });
        }
      } catch {}
      return;
    }
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) processFile(e.dataTransfer.files[0]);
  };

  const handleCardPointerDown = (e: React.PointerEvent) => {
    if (isGuideMode) { e.stopPropagation(); return; }
    if (isQuizMode && onQuizSelect) { e.stopPropagation(); onQuizSelect(node.id); return; }
    if (isLinkingMode) { e.stopPropagation(); onSelect(node.id, e); return; }
    if (readOnly) { e.stopPropagation(); onSelect(node.id, e); return; }
    if (isStream || isMobile) { e.stopPropagation(); onSelect(node.id, e); return; }
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    startClickPosRef.current = { x: e.clientX, y: e.clientY };
    onStartDrag(node.id, e);
    onSelect(node.id, e);
  };

  const handleCardPointerUp = (e: React.PointerEvent) => {
    if (startClickPosRef.current) {
      const dx = Math.abs(e.clientX - startClickPosRef.current.x);
      const dy = Math.abs(e.clientY - startClickPosRef.current.y);
      if (dx < 15 && dy < 15) onClick?.(node.id);
    }
    startClickPosRef.current = null;
    try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { }
  };

  const copyQuestionToNote = (question: string) => {
    if (readOnly) return;
    const newContent = localContent ? `${localContent}\n\n**Q: ${question}**` : `**Q: ${question}**`;
    setLocalContent(newContent);
    onUpdate(node.id, { content: newContent });
  };

  let borderColor = "border-gray-200 dark:border-slate-600/70";
  let shadowClass = isStream ? "shadow-sm" : "shadow-xl";
  let scoreColor = "text-gray-500";
  let headerColor = "bg-gray-50 dark:bg-slate-800";
  let zIndex = "z-0";

  if (isStream) {
    shadowClass = "shadow-sm border-gray-200 dark:border-gray-800 hover:shadow-md transition-all duration-300";
    if (node.analysis) {
      if (node.analysis.score >= 80) borderColor = "border-l-4 border-l-green-500";
      else if (node.analysis.score >= 50) borderColor = "border-l-4 border-l-yellow-400";
      else borderColor = "border-l-4 border-l-red-400";
    } else borderColor = "border-l-4 border-l-gray-300 dark:border-l-gray-600";
    if (isSelected) {
      // Mobile: a soft gray highlight reads calmer in a dense scrolling feed than a bright blue frame.
      borderColor = isMobile
        ? "border-l-4 border-gray-400 dark:border-gray-500 ring-2 ring-gray-200 dark:ring-gray-700"
        : "border-l-4 border-blue-500 ring-4 ring-blue-500/20 dark:ring-blue-400/20";
      shadowClass = "shadow-xl scale-[1.005]";
      headerColor = isMobile ? "bg-gray-50 dark:bg-slate-800/60" : "bg-blue-50/50 dark:bg-blue-900/10";
      zIndex = "z-10";
    } else headerColor = "bg-transparent";
  } else {
    if (node.analysis) {
      if (node.analysis.score >= 80) { borderColor = "border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)]"; scoreColor = "text-green-600 dark:text-green-400"; headerColor = "bg-green-50 dark:bg-green-900/20"; }
      else if (node.analysis.score >= 50) { borderColor = "border-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.3)]"; scoreColor = "text-yellow-600 dark:text-yellow-400"; headerColor = "bg-yellow-50 dark:bg-yellow-900/20"; }
      else { borderColor = "border-red-400 shadow-[0_0_20px_rgba(248,113,113,0.3)]"; scoreColor = "text-red-600 dark:text-red-400"; headerColor = "bg-red-50 dark:bg-red-900/20"; }
    }
    if (isSelected) { borderColor = "border-blue-500 ring-4 ring-blue-500/30 dark:ring-blue-400/30"; headerColor = "bg-blue-50 dark:bg-blue-900/30"; zIndex = "z-20"; }
  }

  if (isGuideMode) {
    if (isGuideSelected) { borderColor = "border-violet-500 ring-4 ring-violet-200 dark:ring-violet-900"; shadowClass = "shadow-[0_0_20px_rgba(139,92,246,0.4)]"; headerColor = "bg-violet-50 dark:bg-violet-950"; }
    else { borderColor = "border-gray-300 dark:border-slate-600 opacity-60"; shadowClass = "shadow-none"; }
  } else if (isQuizMode) {
    if (isQuizSelected) { borderColor = "border-purple-600 ring-4 ring-purple-200 dark:ring-purple-900"; shadowClass = "shadow-[0_0_20px_rgba(147,51,234,0.4)]"; headerColor = "bg-purple-50 dark:bg-purple-950"; }
    else { borderColor = "border-gray-300 dark:border-slate-600 opacity-70"; shadowClass = "shadow-none"; }
  } else if (isDropCandidate) { borderColor = "border-indigo-600 border-4 scale-[1.02] z-50 ring-4 ring-indigo-200"; shadowClass = "shadow-2xl shadow-indigo-500/50"; headerColor = "bg-indigo-50"; }
  else if (isLinkingMode) {
    if (isLinkSource) { borderColor = "border-indigo-500 ring-4 ring-indigo-500/30 animate-pulse"; headerColor = "bg-indigo-50 dark:bg-indigo-900/30"; }
    else { borderColor = "border-indigo-300 border-dashed hover:border-indigo-500 cursor-pointer"; shadowClass = "hover:shadow-[0_0_20px_rgba(99,102,241,0.5)]"; }
  } else if (searchQuery && (titleMatch || contentMatch || tagMatch)) { borderColor = "border-amber-500 ring-4 ring-amber-300/50 dark:ring-amber-500/40 z-30"; shadowClass = "shadow-[0_0_25px_rgba(245,158,11,0.6)]"; headerColor = "bg-amber-50 dark:bg-amber-900/30"; }
  else if (searchQuery && !titleMatch && !contentMatch && !tagMatch) { borderColor = "border-gray-100 dark:border-gray-800 opacity-30"; shadowClass = "shadow-none"; }

  // Zen mode: selecting a card reveals it fully, otherwise only image + content visible
  const showFull = !isZenMode || isSelected;
  const effectiveBorder = isZenMode && !isSelected ? 'border-transparent shadow-none' : borderColor;
  const effectiveShadow = isZenMode && !isSelected ? 'shadow-sm' : shadowClass;

  const mapStyle: React.CSSProperties = {
    left: node.position.x, top: node.position.y, transform: `translate(-50%, -50%)`, pointerEvents: 'auto',
    cursor: (isGuideMode || isQuizMode) ? 'pointer' : (readOnly ? 'default' : (isLinkingMode && !isLinkSource ? 'crosshair' : 'grab')), touchAction: 'none'
  };

  const getWidthClass = () => {
    if (isStream) return compactStream ? 'w-full' : 'w-full mb-6';
    if (!isMap) return 'w-full h-full';
    switch (nodeWidth) {
      case 'wide': return 'w-[480px]';
      case 'extra-wide': return 'w-[640px]';
      case 'standard':
      default: return 'w-80';
    }
  };



  if (lodLevel === 'mini') {
    let dotBg = "bg-blue-50 dark:bg-blue-900/20";
    let dotBorder = "border-blue-300 dark:border-blue-700";
    let dotIndicator = "bg-blue-400";
    if (node.analysis) {
      if (node.analysis.score >= 80) { dotBg = "bg-green-50 dark:bg-green-900/20"; dotBorder = "border-green-300 dark:border-green-700"; dotIndicator = "bg-green-400"; }
      else if (node.analysis.score >= 50) { dotBg = "bg-yellow-50 dark:bg-yellow-900/20"; dotBorder = "border-yellow-300 dark:border-yellow-700"; dotIndicator = "bg-yellow-400"; }
      else { dotBg = "bg-red-50 dark:bg-red-900/20"; dotBorder = "border-red-300 dark:border-red-700"; dotIndicator = "bg-red-400"; }
    }
    return (
      <div
        id={`node-card-inner-${node.id}`}
        data-node-id={node.id}
        className={`absolute flex items-center gap-1.5 px-2 rounded-lg border-2 transition-all duration-300 ${dotBg} ${dotBorder} ${shadowClass} ${zIndex}`}
        style={{ ...mapStyle, height: 30, width: nodeWidth === 'wide' ? 480 : nodeWidth === 'extra-wide' ? 640 : 320 }}
        onPointerDown={handleCardPointerDown}
        onPointerUp={handleCardPointerUp}
      >
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotIndicator}`} />
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate leading-none">{node.title || '…'}</span>
      </div>
    );
  }

  if (lodLevel === 'dot') {
    let dotColor = "bg-blue-500";
    if (node.analysis) {
      if (node.analysis.score >= 80) dotColor = "bg-green-500";
      else if (node.analysis.score >= 50) dotColor = "bg-yellow-500";
      else dotColor = "bg-red-500";
    }
    return (
      <div
        id={`node-card-inner-${node.id}`}
        data-node-id={node.id}
        className={`absolute w-10 h-10 rounded-xl border-2 transition-all duration-300 ${borderColor} ${shadowClass} ${zIndex} ${dotColor}`}
        style={mapStyle}
        onPointerDown={handleCardPointerDown}
        onPointerUp={handleCardPointerUp}
      />
    );
  }

  const renderCardHeader = (isSkeleton?: boolean) => (
    <div className={`px-4 py-2 flex items-center justify-between border-b-0 ${isStream ? 'bg-transparent' : 'border-b dark:border-gray-700 ' + headerColor} transition-colors duration-300 ${isStream ? '' : 'cursor-grab active:cursor-grabbing rounded-t-[14px]'}`}>
      <div className="flex items-center gap-2 flex-1 overflow-hidden relative pr-1">
        {!readOnly && !isStream && !isSkeleton && <GripVertical size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />}
        {!isStream && <div className={`w-3 h-3 rounded-full flex-shrink-0 ${node.analysis ? (node.analysis.score >= 50 ? 'bg-green-400' : 'bg-red-400') : 'bg-blue-400'}`}></div>}
        <div className="relative flex-1 group/title">
          {searchQuery && titleMatch ? <div className="bg-transparent font-bold text-lg md:text-base text-gray-900 dark:text-gray-100 w-full truncate pr-6 relative z-10 py-0.5" dangerouslySetInnerHTML={{ __html: highlightText(node.title, searchQuery) }} />
            : <input
              data-testid="node-title-input"
              type="text"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              disabled={isQuizMode || readOnly || isSkeleton}
              onPointerDown={handleContentInteraction}
              onMouseDown={handleContentInteraction}
              onBlur={() => {
                if (localTitle !== node.title) {
                  onUpdate(node.id, { title: localTitle });
                }
                onEndEditing?.();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              onFocus={onStartEditing}
              className="bg-transparent font-bold text-lg md:text-base outline-none text-gray-900 dark:text-white placeholder-gray-300 w-full truncate pr-6 focus:pr-2 transition-all disabled:text-gray-800 dark:disabled:text-white disabled:bg-transparent relative z-10"
              placeholder={readOnly ? "" : labels.titlePlaceholder}
            />}
          {!isQuizMode && !readOnly && !isSkeleton && !compactStream && <button onClick={handleMagicTitle} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} disabled={isTitleLoading || (!node.content && !node.imageBase64)} className={`absolute right-0 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-violet-100 dark:hover:bg-violet-900/30 text-violet-500 transition-all z-50 cursor-pointer ${isTitleLoading ? 'opacity-100' : 'opacity-0 group-hover/title:opacity-100 focus-within:opacity-100'} disabled:opacity-30 disabled:cursor-not-allowed`}>{isTitleLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}</button>}
        </div>
      </div>
      {!isSkeleton && (
        <div className="flex items-center gap-1 pl-1 relative z-50">
          {isIndexed && !compactStream && <span title="Đã index — có thể dùng Semantic Search" className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0" />}
          {!isQuizMode && !readOnly && !isStream && (
            <>
              <Tip text={language === 'vietnamese' ? 'Thêm nhánh con' : 'Add child node'} side="bottom" align="right">
                <button data-testid="node-add-child-btn" onClick={() => onAddChild(node.id)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-md transition-colors cursor-pointer relative z-50"><GitBranchPlus size={16} /></button>
              </Tip>
              <Tip text={isLinkSource ? (language === 'vietnamese' ? 'Huỷ — đang chờ chọn node đích' : 'Cancel — waiting for target node') : (language === 'vietnamese' ? 'Tạo liên kết chéo' : 'Create cross-link')} side="bottom" align="right">
                <button data-testid="node-link-btn" onClick={() => onStartLink(node.id)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className={`p-2 rounded-md transition-colors cursor-pointer relative z-50 ${isLinkSource ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-indigo-500'}`}>{isLinkSource ? <Unplug size={16} /> : <LinkIcon size={16} />}</button>
              </Tip>
              {onNodeTypeChange && (
                <Tip text={language === 'vietnamese' ? 'Chọn loại node' : 'Select node type'} side="bottom" align="right">
                  <button
                    data-testid="node-type-menu-btn"
                    onClick={(e) => { e.stopPropagation(); setIsNodeTypeModalOpen(true); }}
                    onPointerDown={handleContentInteraction}
                    onMouseDown={handleContentInteraction}
                    className="text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-md transition-colors cursor-pointer relative z-50"
                  >
                    <MoreVertical size={16} />
                  </button>
                </Tip>
              )}
              <Tip text={language === 'vietnamese' ? 'Xoá ghi chú' : 'Delete note'} side="bottom" align="right">
                <button data-testid="node-delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(node.id); }} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-md transition-colors cursor-pointer relative z-50"><Trash2 size={16} /></button>
              </Tip>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      id={`node-card-inner-${node.id}`}
      data-testid="node-card"
      data-node-id={node.id}
      className={`${isMap ? `absolute` : 'relative'} ${getWidthClass()} flex flex-col transition-all duration-200 ${compactStream ? `border-b border-gray-100 dark:border-slate-800 border-l-[3px] ${node.analysis ? (node.analysis.score >= 80 ? 'border-l-green-500' : node.analysis.score >= 50 ? 'border-l-yellow-400' : 'border-l-red-400') : 'border-l-indigo-400'}` : `rounded-2xl bg-white dark:bg-slate-800 border-2 ${effectiveBorder} ${effectiveShadow}`} ${zIndex}`}
      style={isMap ? { ...mapStyle, ...(isLinkHovered ? { boxShadow: '0 0 0 3px #6366f1, 0 0 24px rgba(99,102,241,0.35)', transition: 'box-shadow 0.15s ease' } : {}) } : (isLinkHovered ? { boxShadow: '0 0 0 3px #6366f1, 0 0 24px rgba(99,102,241,0.35)' } : undefined)}
      onPointerDown={handleCardPointerDown}
      onPointerUp={handleCardPointerUp}
      onContextMenu={onNodeContextMenu ? (e) => { e.preventDefault(); onNodeContextMenu(e, node.id); } : undefined}
      onDragEnter={!readOnly ? handleDragEnter : undefined}
      onDragOver={!readOnly ? handleDragOver : undefined}
      onDragLeave={!readOnly ? handleDragLeave : undefined}
      onDrop={!readOnly ? handleDrop : undefined}
    >
      {isMap && scoreMap && (() => {
        // Readable from far away: the number grows as the canvas is zoomed out, within the card.
        const score = node.videoWorthinessScore;
        const hasVideo = !!node.youtubeVideoId || (node.youtubeVideos?.length ?? 0) > 0;
        const fontPx = Math.round(Math.min(120, Math.max(30, 34 / Math.max(scale, 0.05))));
        return (
          <div className={`absolute inset-0 z-[70] rounded-xl flex items-center justify-center pointer-events-none overflow-hidden ${scoreFill(score ?? undefined)} ${score == null ? 'opacity-80' : 'opacity-95'}`}>
            {score != null && <span className="font-black tabular-nums leading-none" style={{ fontSize: fontPx }}>{score}</span>}
            {hasVideo && (
              <span className="absolute rounded-full bg-blue-600 text-white flex items-center justify-center ring-2 ring-white" style={{ top: 8, right: 8, width: fontPx * 0.42, height: fontPx * 0.42 }}>
                <Check size={fontPx * 0.28} strokeWidth={4} />
              </span>
            )}
          </div>
        );
      })()}
      {lodLevel === 'skeleton' ? (
        <>
          {renderCardHeader(true)}
          <div className="px-4 py-4 space-y-4 bg-yellow-50/50 dark:bg-slate-900/50 rounded-b-2xl flex-1 min-h-[180px] transition-all duration-300">
            {hasImages ? (
              <div className="w-full max-h-40 bg-gray-100 dark:bg-slate-700/50 rounded-lg overflow-hidden relative border border-gray-100 dark:border-slate-700 opacity-95 transition-all duration-300">
                <img src={allImages[0].content} className="w-full h-full object-cover" alt="Skeleton cover" />
              </div>
            ) : null}
            <div className="space-y-2.5 py-1">
              {Array.from({ length: Math.min(Math.max(Math.ceil((node.content ?? '').length / 150), 5), 12) }).map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 bg-gray-200/60 dark:bg-slate-700/60 rounded-full"
                  style={{ width: ['90%', '85%', '95%', '40%', '80%', '75%', '92%', '45%', '88%', '82%', '91%', '35%'][i % 12] }}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {isStream && parentTitle && !compactStream && (
            <div className="px-4 pt-3 pb-1 flex items-center">
              <button onClick={(e) => { e.stopPropagation(); if (node.parentId) onJumpToNode(node.parentId); }} className="flex items-center gap-1.5 text-gray-400 hover:text-blue-500 transition-colors text-[10px] font-bold uppercase tracking-wider group/parent">
                <CornerUpLeft size={10} className="group-hover/parent:-translate-y-0.5 transition-transform" />
                <span>{labels.replyTo}: <span className="text-gray-600 dark:text-gray-300 font-semibold">{parentTitle}</span></span>
              </button>
            </div>
          )}

          {isGuideMode && (
            <>
              {/* Full-cover click overlay — captures all clicks anywhere on the card */}
              <div
                className="absolute inset-0 z-[55] rounded-2xl cursor-pointer"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onGuideSelect?.(node.id); }}
              />
              {/* Checkbox badge above the overlay */}
              <div className="absolute -top-3 -right-3 z-[60] transition-transform duration-200 pointer-events-none" style={{ transform: isGuideSelected ? 'scale(1.2)' : 'scale(1)' }}>
                {isGuideSelected
                  ? <CheckCircle2 size={32} className="text-violet-600 bg-white rounded-full shadow-md" fill="white" />
                  : <div className="w-8 h-8 rounded-full border-2 border-gray-300 bg-white shadow-sm" />}
              </div>
            </>
          )}
          {isQuizMode && (
            <div className="absolute -top-3 -right-3 z-50 transition-transform duration-200" style={{ transform: isQuizSelected ? 'scale(1.2)' : 'scale(1)' }}>
              {isQuizSelected ? <CheckCircle2 size={32} className="text-purple-600 bg-white rounded-full shadow-md" fill="white" /> : <div className="w-8 h-8 rounded-full border-2 border-gray-300 bg-white hover:border-purple-500 transition-colors shadow-sm"></div>}
            </div>
          )}

          {isDragActive && !readOnly && (
            <div className="absolute inset-0 z-50 bg-blue-50/90 dark:bg-slate-800/95 backdrop-blur-sm rounded-2xl border-4 border-dashed border-blue-500 flex flex-col items-center justify-center pointer-events-none animate-in fade-in duration-200">
              <Upload size={48} className="text-blue-500 mb-2" />
              <p className="font-bold text-blue-600 dark:text-blue-400 text-lg">{labels.dropImage}</p>
            </div>
          )}

          {showFull && renderCardHeader(false)}

          {!isLabel && (hasImages || displayExpanded) && (
            <div className={`px-4 pt-1 ${isQuizMode ? 'pointer-events-none opacity-90' : ''}`}>
              {allImages.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <div ref={scrollContainerRef} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className={(isZenMode || compactStream) ? 'flex flex-col gap-2' : 'flex overflow-x-auto gap-2 pb-2 snap-x snap-mandatory note-scroll'}>
                    {allImages.map((img, index) => (
                      <div
                        key={img.id}
                        className={`relative group/image ${isZenMode ? 'w-full' : 'flex-shrink-0 snap-center'}`}
                        style={isZenMode ? undefined : { width: '100%' }}
                        draggable={!readOnly && !isQuizMode}
                        onDragStart={!readOnly && !isQuizMode ? (e) => {
                          e.stopPropagation();
                          e.dataTransfer.effectAllowed = 'copy';
                          e.dataTransfer.setData('application/x-studyboard-image', JSON.stringify({ content: img.content, mimeType: img.mimeType }));
                        } : undefined}
                      >
                        <div
                          className={`bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-x-hidden relative cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all ${(isZenMode || expandCycle === 2) ? 'overflow-y-visible' : 'overflow-y-auto max-h-72'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isMobile && onMobileImageOpen) {
                              onMobileImageOpen(img.content);
                            } else {
                              setImageGallery({ isOpen: true, initialIndex: index });
                            }
                          }}
                          onWheel={(e) => e.stopPropagation()}
                        >
                          <img src={img.content} alt="Attachment" className={`w-full block ${isMobile ? 'max-h-48 object-contain' : 'h-auto'}`} draggable={false} />
                          {!compactStream && <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = ((expandCycle + 1) % 3) as 0 | 1 | 2;
                              setExpandCycle(next);
                              onExpandCycleChange?.(node.id, next);
                            }}
                            onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction}
                            className={`absolute top-2 right-2 text-white p-1 rounded-md transition-all z-10 ${expandCycle === 0 ? 'bg-black/40 hover:bg-black/60' : expandCycle === 1 ? 'bg-blue-500/80 hover:bg-blue-600/90' : 'bg-amber-500/80 hover:bg-amber-600/90'}`}
                            title={expandCycle === 0 ? 'Mở toàn bộ text' : expandCycle === 1 ? 'Mở toàn bộ ảnh' : 'Thu gọn'}
                          >
                            {expandCycle === 0 ? <ChevronDown size={11} /> : expandCycle === 1 ? <Maximize2 size={11} /> : <Minimize2 size={11} />}
                          </button>}
                          {img.type === 'drawing' && <div className="absolute top-2 left-2 bg-purple-500/80 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">SKETCH</div>}
                          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">{index + 1} / {allImages.length}</div>
                        </div>
                        {!isQuizMode && !readOnly && !compactStream && <button onClick={() => { if (img.id === 'main') removeMainImage(); else if (img.id === 'drawing') onUpdate(node.id, { drawingBase64: null }); else removeAttachment(img.id); }} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow opacity-0 group-hover/image:opacity-100 transition-opacity z-50 cursor-pointer"><X size={12} /></button>}
                      </div>
                    ))}
                    {!isQuizMode && !readOnly && !isZenMode && !isMobile && displayExpanded && (
                      <div onClick={() => fileInputRef.current?.click()} className="flex-shrink-0 w-24 bg-gray-50 dark:bg-slate-800 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"><ImagePlus size={20} className="text-gray-400" /><span className="text-[10px] text-gray-400 font-bold mt-1">{labels.addMoreImages}</span></div>
                    )}
                  </div>
                </div>
              ) : (!isQuizMode && !readOnly && !isLabel && !isZenMode && displayExpanded && (
                <div onClick={() => fileInputRef.current?.click()} onPointerDown={handleContentInteraction} className="w-full h-16 border-2 border-dashed border-gray-200 dark:border-slate-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-all group mt-2"><div className="flex items-center gap-2"><Upload size={16} className="text-gray-400 group-hover:text-blue-500" /><span className="text-[10px] uppercase font-semibold text-gray-400 group-hover:text-blue-500 tracking-wide">{labels.addImage}</span></div></div>
              ))}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </div>
          )}

          <div className={`px-4 pb-4 pt-3 flex flex-col gap-3 relative ${isMap ? '' : 'flex-1'} ${isQuizMode ? 'pointer-events-none' : ''}`}>
            {!compactStream && ((node.content?.length ?? 0) > 0 || node.imageBase64 || node.attachments?.length) && (
              <div className={`absolute ${isStream ? 'right-3 top-3' : 'right-2 top-2'} z-50`}>
                <button data-testid="node-expand-btn" onClick={() => onUpdate(node.id, { isExpanded: !isContentExpanded })} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="text-gray-400 hover:text-blue-500 p-1 rounded-full bg-white/80 dark:bg-slate-800/80 shadow-sm border border-gray-100 dark:border-slate-600 hover:bg-blue-50 transition-all relative z-50 cursor-pointer">{isContentExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
              </div>
            )}

            {/* === HASHTAG PILLS === */}
            {!compactStream && showFull && !isLabel && ((node.tags && node.tags.length > 0) || isExtractingTags || (!readOnly && !isQuizMode && !!node.imageBase64)) && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1 pb-0.5" onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction}>
                <Tag size={11} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                {isExtractingTags && (
                  <span className="flex items-center gap-1 text-[10px] text-indigo-400 animate-pulse">
                    <Loader2 size={10} className="animate-spin" />
                    Đang nhận diện...
                  </span>
                )}
                {/* Manual extract button — shown when node has image, not extracting */}
                {!isExtractingTags && !readOnly && node.imageBase64 && (
                  <button
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-full border border-dashed border-indigo-200 dark:border-indigo-700 hover:border-indigo-400 transition-colors cursor-pointer"
                    title="Tự động nhận diện hashtag từ ảnh"
                    onClick={(e) => { e.stopPropagation(); triggerExtractTags([ ...(node.imageBase64 ? [{ base64: node.imageBase64, mimeType: node.imageMimeType || 'image/png' }] : []), ...(node.attachments || []).filter(a => a.type === 'image').map(a => ({ base64: a.content, mimeType: a.mimeType || 'image/png' })) ]); }}
                    onPointerDown={handleContentInteraction}
                  >
                    <Sparkles size={9} /> AI tags
                  </button>
                )}
                {(isMobile ? (node.tags || []).slice(0, 3) : (node.tags || [])).map((tag, idx) => (
                  <span
                    key={idx}
                    className="group/tag inline-flex items-center gap-0.5 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-[11px] font-semibold rounded-full border border-indigo-200 dark:border-indigo-700/50 cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors select-none min-w-0"
                    onClick={(e) => { e.stopPropagation(); onTagSearch?.(tag); }}
                    title={tag}
                  >
                    <span className="truncate whitespace-nowrap max-w-[120px]">{tag}</span>
                    {!readOnly && !isQuizMode && (
                      <button
                        className="ml-0.5 opacity-0 group-hover/tag:opacity-100 text-indigo-400 hover:text-red-500 transition-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newTags = (node.tags || []).filter((_, i) => i !== idx);
                          onUpdate(node.id, { tags: newTags });
                        }}
                      >
                        <X size={9} />
                      </button>
                    )}
                  </span>
                ))}
                {isMobile && (node.tags || []).length > 3 && (
                  <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 text-[11px] font-semibold rounded-full border border-gray-200 dark:border-slate-600">
                    +{(node.tags || []).length - 3}
                  </span>
                )}
                {!readOnly && !isQuizMode && addTagInput === null && (
                  <button
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-full border border-dashed border-gray-300 dark:border-slate-600 hover:border-indigo-300 transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); setAddTagInput(''); setTimeout(() => addTagInputRef.current?.focus(), 50); }}
                    onPointerDown={handleContentInteraction}
                  >
                    <Plus size={9} /> tag
                  </button>
                )}
                {!readOnly && addTagInput !== null && (
                  <input
                    ref={addTagInputRef}
                    type="text"
                    value={addTagInput}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-indigo-300 dark:border-indigo-600 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 outline-none w-24 focus:ring-1 focus:ring-indigo-300"
                    placeholder="#tag"
                    onChange={(e) => setAddTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && addTagInput.trim()) {
                        const raw = addTagInput.trim();
                        const formatted = raw.startsWith('#') ? raw : `#${raw}`;
                        const newTags = [...(node.tags || []), formatted];
                        onUpdate(node.id, { tags: newTags });
                        setAddTagInput(null);
                      } else if (e.key === 'Escape') {
                        setAddTagInput(null);
                      }
                    }}
                    onBlur={() => {
                      if (addTagInput.trim()) {
                        const raw = addTagInput.trim();
                        const formatted = raw.startsWith('#') ? raw : `#${raw}`;
                        onUpdate(node.id, { tags: [...(node.tags || []), formatted] });
                      }
                      setAddTagInput(null);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            )}

            {!compactStream && !isLabel && node.suggestedQuestions && node.suggestedQuestions.length > 0 && displayExpanded && (

              <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/30 rounded-lg p-3 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 font-bold text-indigo-700 dark:text-indigo-300"><LifeBuoy size={12} /><span className="uppercase tracking-wider text-[10px]">{labels.studyFramework}</span></div>
                  {!readOnly && <button onClick={() => onUpdate(node.id, { suggestedQuestions: undefined })} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-200 p-1"><X size={10} /></button>}
                </div>
                <ul className="space-y-1.5">
                  {node.suggestedQuestions.map((q, idx) => (
                    <li key={idx} className={`flex gap-2 items-start group/question p-1 rounded transition-colors ${!readOnly ? 'cursor-pointer hover:bg-white/50 dark:hover:bg-slate-800/50' : ''}`} onClick={() => copyQuestionToNote(q)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction}>
                      <span className="font-bold text-indigo-500 dark:text-indigo-400 text-xs mt-0.5">{idx + 1}.</span>
                      <span className="flex-1 text-gray-700 dark:text-gray-300 text-xs leading-relaxed">{q}</span>
                      {!readOnly && <Copy size={10} className="text-gray-400 opacity-0 group-hover/question:opacity-100 mt-1 flex-shrink-0" />}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(displayExpanded ? (
              <div className="relative group/edit">

                {/* Inline formatting toolbar — only in non-map modes */}
                {isEditing && !readOnly && !isMap && (
                  <div className="flex items-center gap-1 mb-1 animate-in fade-in zoom-in duration-200">
                    <button onClick={handleBoldToggle} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className={`p-2 rounded relative z-50 cursor-pointer transition-colors ${boldMode ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700'}`} title="Bold (Ctrl+B)"><Bold size={12} /></button>
                    <button onClick={() => handleInsertMarkdown('_', '_')} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded relative z-50 cursor-pointer"><Italic size={12} /></button>
                    <button onClick={() => handleInsertMarkdown('\n- ', '')} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded relative z-50 cursor-pointer"><List size={12} /></button>
                    {boldMode && <span className="text-[10px] font-bold text-blue-500 dark:text-blue-400 px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 rounded animate-pulse select-none">BOLD</span>}
                  </div>
                )}

                {/* Content area */}
                {(!isEditing || isMap) && node.content && node.content.length > 0 ? (
                  <div
                    data-testid="node-content-view"
                    onClick={(e) => {
                      if (isLinkingMode || isQuizMode) return;
                      e.stopPropagation();
                      if (isMap) { onExpandNote(node.id); }
                      else if (!readOnly && !isMobile) { setIsEditing(true); }
                    }}
                    onPointerDown={isMobile ? undefined : handleContentInteraction}
                    onMouseDown={isMobile ? undefined : handleContentInteraction}
                    onWheel={(e) => e.stopPropagation()}
                    className={`w-full ${compactStream ? '' : 'min-h-[80px]'} text-base md:text-sm ${compactStream ? 'px-0 py-1' : 'p-3'} ${compactStream ? 'bg-transparent' : 'bg-yellow-50/50 dark:bg-slate-900/50'} border border-transparent ${compactStream ? '' : 'rounded-lg'} text-gray-700 dark:text-gray-200 ${compactStream ? 'overflow-hidden' : isStream ? 'overflow-y-visible' : 'note-scroll overflow-y-auto max-h-[320px]'} ${(isMap || isMobile) && !readOnly && !isQuizMode ? `cursor-pointer ${bottomPad} hover:bg-yellow-100/60 dark:hover:bg-slate-800/80 transition-colors` : !isQuizMode && !readOnly ? `cursor-text ${bottomPad} hover:border-yellow-100 dark:hover:border-slate-600 transition-colors` : 'pb-3'}`}
                  >
                    <div className={`prose md:prose-sm dark:prose-invert max-w-none leading-relaxed pointer-events-none [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4 ${compactStream ? 'line-clamp-4' : ''}`} dangerouslySetInnerHTML={{ __html: parseMarkdown(searchQuery ? highlightText(node.content, searchQuery) : node.content) }} />
                  </div>
                ) : isMap && !isEditing ? (
                  /* Map mode + empty content: placeholder opens modal */
                  <div
                    onClick={(e) => { e.stopPropagation(); if (!isQuizMode) onExpandNote(node.id); }}
                    onPointerDown={handleContentInteraction}
                    onMouseDown={handleContentInteraction}
                    className={`w-full min-h-[60px] p-3 rounded-lg border border-dashed border-gray-200 dark:border-slate-700 text-sm text-gray-400 italic flex items-center gap-2 transition-all ${!readOnly && !isQuizMode ? 'cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 hover:text-blue-400' : ''}`}
                  >
                    <Maximize2 size={13} className="flex-shrink-0 opacity-60" />
                    <span>{language === 'vietnamese' ? 'Nhấn để mở và viết...' : 'Click to open and write...'}</span>
                  </div>
                ) : (
                  /* Non-map mode: inline textarea */
                  <textarea data-testid="node-content-input" ref={textareaRef} value={localContent} onChange={(e) => {
                    const newText = e.target.value;
                    setLocalContent(newText);
                    if (debouncedUpdateRef.current) clearTimeout(debouncedUpdateRef.current);
                    debouncedUpdateRef.current = setTimeout(() => {
                      onUpdate(node.id, { content: newText });
                    }, 400);
                  }} onKeyDown={handleKeyDown} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} onWheel={(e) => e.stopPropagation()} onBlur={handleTextareaBlur} onFocus={onStartEditing} autoFocus={isEditing} placeholder={readOnly ? "" : (isRecording ? labels.recording : labels.placeholder)} rows={1} disabled={isQuizMode || readOnly} className={`w-full min-h-[80px] text-base md:text-sm p-3 pb-8 bg-yellow-50/50 dark:bg-slate-900/50 border rounded-lg resize-none outline-none text-gray-700 dark:text-gray-200 transition-colors note-scroll disabled:bg-transparent disabled:text-gray-800 dark:disabled:text-gray-200 disabled:border-transparent relative z-50 ${boldMode ? 'border-blue-400 dark:border-blue-400 focus:border-blue-500 dark:focus:border-blue-400' : 'border-yellow-100 dark:border-slate-600 focus:border-yellow-300 dark:focus:border-blue-500'}`} />
                )}

                {/* Expand button: always-visible blue pill in map mode */}
                {isMap && !readOnly && !isQuizMode && (node.content?.length ?? 0) > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onExpandNote(node.id); }}
                    onPointerDown={handleContentInteraction}
                    onMouseDown={handleContentInteraction}
                    className="absolute bottom-2 right-2 z-[60] flex items-center gap-1 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm hover:shadow transition-all cursor-pointer"
                    title={language === 'vietnamese' ? 'Mở để chỉnh sửa' : 'Open to edit'}
                  >
                    <Maximize2 size={11} />
                    <span>{language === 'vietnamese' ? 'Mở' : 'Edit'}</span>
                  </button>
                )}

                {/* Non-map expand button: subtle on desktop hover only (mobile has dedicated Open button) */}
                {!isMap && !isMobile && (
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 z-[60] transition-opacity opacity-0 group-hover/edit:opacity-100">
                    <Tip text={language === 'vietnamese' ? 'Mở để chỉnh sửa' : 'Open to edit'} side="top" align="right">
                      <button onClick={() => onExpandNote(node.id)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all cursor-pointer bg-white/50 dark:bg-slate-800/50 hover:bg-blue-100 dark:hover:bg-blue-900/30"><Maximize2 size={14} /></button>
                    </Tip>
                  </div>
                )}
              </div>
            ) : (
              <div onClick={() => onUpdate(node.id, { isExpanded: true })} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="w-full p-2.5 bg-yellow-50/30 border border-transparent rounded-lg text-sm text-gray-400 italic cursor-pointer hover:bg-yellow-50 truncate select-none flex items-center gap-2"><span className="truncate flex-1">{searchQuery && contentMatch ? <span dangerouslySetInnerHTML={{ __html: highlightText(node.content, searchQuery) }} /> : node.content || "(Collapsed Text)"}</span></div>
            ))}

            {isSourceRoot && !hasChildNodes && !readOnly && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddChild(node.id); }}
                onPointerDown={handleContentInteraction}
                onMouseDown={handleContentInteraction}
                className="w-full mt-3 py-2 px-3 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-200 dark:border-blue-800 text-blue-500 dark:text-blue-400 text-xs font-semibold hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all cursor-pointer"
              >
                <GitBranchPlus size={13} />
                {language === 'vietnamese' ? '+ Tạo nhánh đầu tiên' : '+ Create first branch'}
              </button>
            )}

            <div className={`flex items-center mt-auto ${compactStream ? 'pt-1 justify-end' : `pt-3 justify-between ${isStream ? 'border-t dark:border-slate-700' : ''}`} ${!showFull ? 'hidden' : ''}`}>
              <div className="flex gap-2 relative z-50">
                {!readOnly && (
                  <>
                    {isStream && !compactStream && <button data-testid="node-add-child-btn" onClick={() => onAddChild(node.id)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} disabled={isQuizMode} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 px-2 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-30 cursor-pointer" title={labels.addBranch}><GitBranchPlus size={14} />{isStream && <span className="hidden sm:inline">{labels.addBranch}</span>}</button>}

                    {isStream && !compactStream && <button onClick={() => onStartLink(node.id)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-1.5 rounded-lg transition-colors cursor-pointer ${isLinkSource ? 'bg-indigo-100 text-indigo-600' : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'}`} title={labels.linkNode}>{isLinkSource ? <Unplug size={14} /> : <><LinkIcon size={14} /><span className="hidden sm:inline">{labels.linkNode}</span></>}</button>}
                  </>
                )}
              </div>
              <div className="flex gap-2 relative z-50">
                {isMobile && !isQuizMode && !isLinkingMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onExpandNote(node.id); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 text-sm font-semibold rounded-full transition-all active:scale-95 active:bg-indigo-100 cursor-pointer min-h-[40px]"
                  >
                    <ArrowUpRight size={13} />
                    {language === 'vietnamese' ? 'Mở' : 'Open'}
                  </button>
                )}
                {!compactStream && !isLabel && ((node.content ?? '').length > 3 || node.imageBase64 || (node.attachments && node.attachments.length > 0)) && !readOnly && (
                  node.isAnalyzing
                    ? <div className="flex items-center justify-center p-2"><Loader2 size={16} className="animate-spin text-indigo-500" /></div>
                    : <Tip text={language === 'vietnamese' ? 'AI chấm điểm phần giải thích của bạn và cho phản hồi chi tiết' : 'AI scores your explanation and gives detailed feedback'}><button data-testid="node-ai-check-btn" onClick={() => onAnalyze(node.id, 'simple')} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} disabled={isQuizMode} className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-full shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-30 cursor-pointer"><Brain size={14} /><span className="hidden sm:inline">AI Check</span></button></Tip>
                )}

                {/* Recall / Memory Gym for this note */}
                {!compactStream && !isLabel && !readOnly && node.analysis && (
                  <Tip text={language === 'vietnamese' ? 'Giải thích lại ghi chú bằng giọng nói — AI chấm và lưu lịch sử ôn tập' : 'Re-explain the note by voice — AI scores it and tracks your recall history'}>
                    <button
                      onClick={() => onStartReview && onStartReview(node.id)}
                      onPointerDown={handleContentInteraction}
                      onMouseDown={handleContentInteraction}
                      className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-full shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-30 cursor-pointer"
                    >
                      <Mic size={14} />
                      <span className="hidden sm:inline">{language === 'vietnamese' ? 'Ôn lại' : 'Recall'}</span>
                    </button>
                  </Tip>
                )}


                {isStream && !readOnly && !compactStream && <button onClick={(e) => { e.stopPropagation(); onDelete(node.id); }} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>}
              </div>
            </div>

            {!isLabel && node.analysis && displayExpanded && compactStream && (
              <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
                <button
                  className="flex items-center gap-2 w-full py-1 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); setIsMobileScoreExpanded(v => !v); }}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <span className={`text-sm font-bold ${scoreColor}`}>{node.analysis.score}%</span>
                  <span className="text-sm text-gray-400 dark:text-gray-500">
                    {node.analysis.score >= 80
                      ? (language === 'vietnamese' ? '· Rất tốt' : '· Very good')
                      : node.analysis.score >= 50
                      ? (language === 'vietnamese' ? '· Khá ổn' : '· Fair')
                      : (language === 'vietnamese' ? '· Cần cải thiện' : '· Needs work')}
                  </span>
                  <ChevronDown size={12} className={`ml-auto text-gray-400 transition-transform duration-200 ${isMobileScoreExpanded ? 'rotate-180' : ''}`} />
                </button>
                {isMobileScoreExpanded && (
                  <RenderedFeedback text={node.analysis.feedback} className={`text-sm leading-relaxed mt-2 animate-in slide-in-from-top-1 duration-150 prose prose-sm dark:prose-invert max-w-none ${node.analysis.score >= 80 ? 'text-green-800 dark:text-green-300' : node.analysis.score >= 50 ? 'text-yellow-800 dark:text-yellow-300' : 'text-red-800 dark:text-red-300'}`} />
                )}
              </div>
            )}

            {!isLabel && node.analysis && displayExpanded && !compactStream && (() => {
              const a = node.analysis;
              // Derive verdict for backward compat (old analyses without verdict field)
              const verdict = a.verdict ?? (a.score >= 80 ? 'pass' : a.score >= 50 ? 'minor_issues' : 'critical_issues');
              const moveOn = a.moveOn ?? (verdict !== 'critical_issues');
              const hasCritical = (a.criticalIssues?.length ?? 0) > 0;
              const hasMinor = (a.minorIssues?.length ?? 0) > 0;
              const hasStructured = hasCritical || hasMinor || (a.strengths?.length ?? 0) > 0;
              const verdictLabel = verdict === 'pass'
                ? (language === 'vietnamese' ? '🟢 Đạt' : '🟢 Pass')
                : verdict === 'minor_issues'
                ? (language === 'vietnamese' ? '🟡 Lỗi nhỏ' : '🟡 Minor issues')
                : (language === 'vietnamese' ? '🔴 Lỗi nghiêm trọng' : '🔴 Critical issues');
              const verdictColor = verdict === 'pass' ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : verdict === 'minor_issues' ? 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
              const isLegacyCheck = !node.aiCheckVersion;
              return (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300 pt-3 border-t dark:border-slate-700">
                  {/* Header: verdict + move on + score */}
                  <div className="flex items-center justify-between mb-2 cursor-pointer select-none relative z-50" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction}>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${verdictColor}`}>{verdictLabel}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${moveOn ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                        {moveOn ? (language === 'vietnamese' ? '✓ Move on' : '✓ Move on') : (language === 'vietnamese' ? '✗ Fix first' : '✗ Fix first')}
                      </span>
                      {isLegacyCheck && <span className="text-[10px] px-1.5 py-0.5 rounded border text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700">⏰ Legacy</span>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-black ${scoreColor}`}>{a.score}%</span>
                      <ChevronDown size={13} className={`text-gray-400 transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  <div className="h-1 w-full bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden mb-2">
                    <div className={`h-full transition-all duration-1000 ${verdict === 'pass' ? 'bg-green-500' : verdict === 'minor_issues' ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${a.score}%` }} />
                  </div>

                  {isFeedbackExpanded && (
                    <div className="space-y-2 animate-in slide-in-from-top-1 duration-200">
                      {/* Summary */}
                      <div className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
                        <RenderedFeedback text={a.feedback} className="prose prose-sm dark:prose-invert max-w-none" />
                      </div>

                      {/* Critical issues */}
                      {hasCritical && (
                        <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/10 p-2 space-y-1.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">{language === 'vietnamese' ? '🔴 Lỗi nghiêm trọng' : '🔴 Critical issues'}</p>
                          {a.criticalIssues!.map((item, i) => (
                            <div key={i}>
                              {item.quote && <p className="text-[10px] italic text-red-500 dark:text-red-400 border-l-2 border-red-400 pl-1.5 mb-0.5">"{item.quote}"</p>}
                              <p className="text-[11px] text-red-800 dark:text-red-300 leading-snug">{item.explanation}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Minor issues */}
                      {hasMinor && (
                        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800/50 bg-yellow-50 dark:bg-yellow-900/10 p-2 space-y-1.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-yellow-700 dark:text-yellow-400">{language === 'vietnamese' ? '🟡 Lỗi nhỏ / chưa chặt' : '🟡 Minor issues'}</p>
                          {a.minorIssues!.map((item, i) => (
                            <div key={i}>
                              {item.quote && <p className="text-[10px] italic text-yellow-600 dark:text-yellow-400 border-l-2 border-yellow-400 pl-1.5 mb-0.5">"{item.quote}"</p>}
                              <p className="text-[11px] text-yellow-900 dark:text-yellow-300 leading-snug">{item.explanation}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Strengths */}
                      {(a.strengths?.length ?? 0) > 0 && (
                        <div className="rounded-lg border border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10 p-2 space-y-1">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-green-700 dark:text-green-400">{language === 'vietnamese' ? '✓ Điểm tốt' : '✓ Strengths'}</p>
                          {a.strengths!.map((s, i) => <p key={i} className="text-[11px] text-green-900 dark:text-green-300 leading-snug">• {s}</p>)}
                        </div>
                      )}

                      {/* Nuances */}
                      {(a.nuances?.length ?? 0) > 0 && (
                        <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/50 p-2 space-y-1">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{language === 'vietnamese' ? '💡 Ghi chú thêm' : '💡 Deeper notes'}</p>
                          {a.nuances!.map((n, i) => <p key={i} className="text-[11px] text-gray-600 dark:text-gray-400 leading-snug">• {n}</p>)}
                        </div>
                      )}

                      {/* Fallback: old detailedFeedback */}
                      {!hasStructured && a.detailedFeedback && (
                        <button
                          onClick={() => setIsDetailModalOpen(true)}
                          onPointerDown={handleContentInteraction}
                          onMouseDown={handleContentInteraction}
                          className="w-full flex items-center justify-center gap-2 py-2 border border-indigo-200 dark:border-indigo-900/50 rounded-lg text-indigo-600 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                        >
                          <Eye size={12} />
                          {labels.viewDetails}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Review question generation in-progress hint */}
            {!compactStream && showFull && !isLabel && node.analysis && node.reviewQuestionStatus === 'generating' && (
              <div className="w-full flex items-center gap-2 px-3 py-2 mt-1 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-lg">
                <Loader2 size={11} className="text-blue-500 flex-shrink-0 animate-spin" />
                <span className="text-[11px] text-blue-600 dark:text-blue-400">
                  {language === 'vietnamese' ? 'Đang tạo câu hỏi ôn tập...' : 'Preparing review questions...'}
                </span>
              </div>
            )}

            {/* Learning Status Row — shows after AI Check when questions exist */}
            {!compactStream && showFull && !isLabel && node.analysis && node.reviewQuestionCount != null && node.reviewQuestionCount > 0 && onViewNoteQuestions && (
              <button
                onClick={() => onViewNoteQuestions(node.id)}
                onPointerDown={handleContentInteraction}
                onMouseDown={handleContentInteraction}
                className="w-full flex items-center gap-2 px-3 py-2 mt-1 bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg transition-colors group/ql relative z-50"
              >
                <Zap size={12} className="text-amber-500 flex-shrink-0" />
                <span className="text-[11px] font-medium text-amber-700 dark:text-amber-300 flex-1 text-left">
                  {node.reviewQuestionCount} {language === 'vietnamese' ? 'câu hỏi ôn tập' : 'review questions'}
                </span>
                <span className="text-[10px] text-amber-500 dark:text-amber-400 font-semibold group-hover/ql:underline">
                  {language === 'vietnamese' ? 'Xem →' : 'View →'}
                </span>
              </button>
            )}

            {/* Video link badge */}
            {!compactStream && showFull && !isLabel && node.videoUrl && (
              <a
                href={node.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={handleContentInteraction}
                onMouseDown={handleContentInteraction}
                className="w-full flex items-center gap-2 px-3 py-2 mt-1 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg transition-colors group/vl relative z-50 no-underline"
              >
                <span className="text-base flex-shrink-0">🎥</span>
                <span className="text-[11px] font-medium text-red-700 dark:text-red-300 flex-1 text-left truncate">
                  {node.videoTitle || (language === 'vietnamese' ? 'Video học' : 'Video')}
                  {node.videoDuration && <span className="ml-1 text-red-400 dark:text-red-500 font-normal">({node.videoDuration})</span>}
                </span>
                <span className="text-[10px] text-red-500 dark:text-red-400 font-semibold group-hover/vl:underline flex-shrink-0">
                  {language === 'vietnamese' ? 'Xem →' : 'Watch →'}
                </span>
              </a>
            )}

            {showFull && linkedNodes.length > 0 && (() => {
              const visibleCount = compactStream
                ? linkedNodes.filter(ln => ln.relationType !== 'parent' && ln.relationType !== 'child').length
                : linkedNodes.length;
              if (visibleCount === 0) return null;
              const parents = compactStream ? [] : linkedNodes.filter(ln => ln.relationType === 'parent');
              const children = compactStream ? [] : linkedNodes.filter(ln => ln.relationType === 'child');
              const peers = linkedNodes.filter(ln => ln.relationType !== 'parent' && ln.relationType !== 'child');
              const renderGroup = (
                nodes: typeof linkedNodes,
                symbol: string,
                symbolColor: string,
                bgColor: string,
                hoverBg: string,
                dividerColor: string,
              ) => nodes.length > 0 && (
                <div className="flex items-start gap-1.5">
                  {!compactStream && <span className={`text-[12px] font-bold ${symbolColor} w-4 shrink-0 mt-0.5`}>{symbol}</span>}
                  <div className="flex flex-wrap gap-1">
                    {nodes.map(ln => (
                      <div key={ln.linkId} className={`flex items-center ${bgColor} ${compactStream ? 'text-sm' : 'text-[10px]'} font-medium rounded-full border relative z-50`}>
                        <Tip text={`${language === 'vietnamese' ? 'Đến ghi chú' : 'Go to note'}: ${ln.title || 'Untitled'}`} side="top">
                          <button onClick={() => onJumpToNode(ln.id)} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className={`${compactStream ? 'px-3 py-1' : 'px-2 py-0.5'} ${hoverBg} ${readOnly ? 'rounded-full' : 'rounded-l-full'} transition-colors cursor-pointer`}>
                            <span className={`${compactStream ? 'max-w-[140px]' : 'max-w-[90px]'} truncate block`}>{ln.title || 'Untitled'}</span>
                          </button>
                        </Tip>
                        {!readOnly && <><div className={`w-px h-3 ${dividerColor}`} /><Tip text={language === 'vietnamese' ? 'Xoá liên kết' : 'Remove link'} side="top"><button onClick={(e) => { e.stopPropagation(); onUnlink(ln.id); }} onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction} className="px-1.5 py-0.5 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400 rounded-r-full transition-colors cursor-pointer"><X size={10} /></button></Tip></>}
                      </div>
                    ))}
                  </div>
                </div>
              );
              return (
                <div className="pt-2 border-t dark:border-gray-700 border-dashed">
                  <span className={`${compactStream ? 'text-xs' : 'text-[10px]'} font-bold text-gray-400 uppercase tracking-wider`}>{labels.related}</span>
                  <div className="mt-1.5 flex flex-col gap-1.5">
                    {renderGroup(parents,  '↑', 'text-amber-500',  'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/50',  'hover:bg-amber-100 dark:hover:bg-amber-900/50',  'bg-amber-200 dark:bg-amber-800')}
                    {renderGroup(children, '↓', 'text-emerald-500', 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/50', 'hover:bg-emerald-100 dark:hover:bg-emerald-900/50', 'bg-emerald-200 dark:bg-emerald-800')}
                    {renderGroup(peers,    '↔', 'text-indigo-500',  'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 border-indigo-100 dark:border-indigo-900/50',  'hover:bg-indigo-100 dark:hover:bg-indigo-900/50',  'bg-indigo-200 dark:bg-indigo-800')}
                  </div>
                </div>
              );
            })()}
          {!compactStream && displayExpanded && !isLabel && (lastSavedTime || syncState === 'saving') && (
            <div className="flex items-center gap-1 pt-1.5 select-none" onPointerDown={handleContentInteraction} onMouseDown={handleContentInteraction}>
              {syncState === 'saving' ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse shrink-0" />
                  <span className="text-[10px] text-blue-400 dark:text-blue-500">
                    {language === 'vietnamese' ? 'Đang lưu...' : 'Saving...'}
                  </span>
                </>
              ) : lastSavedTime ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {language === 'vietnamese'
                      ? `Lưu lúc ${lastSavedTime.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}`
                      : `Saved ${lastSavedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                  </span>
                </>
              ) : null}
            </div>
          )}
          </div>
        </>
      )}

      {/* Detailed Feedback Modal */}
      {isDetailModalOpen && (
        <DetailedFeedbackModal
          node={node}
          onClose={() => setIsDetailModalOpen(false)}
          onReanalyze={(nodeId) => onAnalyze(nodeId, 'detailed')}
          language={language}
        />
      )}

      {/* Image Gallery Modal */}
      {imageGallery?.isOpen && (
        <ImageGalleryModal
          images={allImages}
          initialIndex={imageGallery.initialIndex}
          onClose={() => setImageGallery(null)}
        />
      )}




      {/* Node Type Selection Modal */}
      {isNodeTypeModalOpen && onNodeTypeChange && (
        <NodeTypeModal
          isOpen={isNodeTypeModalOpen}
          currentType={node.nodeType || 'note'}
          onSelect={(newType) => onNodeTypeChange(node.id, newType)}
          onClose={() => setIsNodeTypeModalOpen(false)}
          language={language}
        />
      )}
    </div>
  );
};

export const NodeCard = React.memo(NodeCardComponent, (prev, next) => {
  // Standard property comparison for Performance Overdrive
  if (prev.node !== next.node) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isLinkSource !== next.isLinkSource) return false;
  if (prev.isLinkingMode !== next.isLinkingMode) return false;
  if (prev.isDropCandidate !== next.isDropCandidate) return false;
  if (prev.isMagicExpanding !== next.isMagicExpanding) return false;
  if (prev.isQuizMode !== next.isQuizMode) return false;
  if (prev.isQuizSelected !== next.isQuizSelected) return false;
  if (prev.isGuideMode !== next.isGuideMode) return false;
  if (prev.isGuideSelected !== next.isGuideSelected) return false;
  if (prev.scale !== next.scale) return false;
  if (prev.scoreMap !== next.scoreMap) return false;
  if (prev.language !== next.language) return false;
  if (prev.layoutMode !== next.layoutMode) return false;
  if (prev.searchQuery !== next.searchQuery) return false;
  if (prev.readOnly !== next.readOnly) return false;
  if (prev.nodeWidth !== next.nodeWidth) return false;
  if (prev.lodLevel !== next.lodLevel) return false;
  if (JSON.stringify(prev.linkedNodes) !== JSON.stringify(next.linkedNodes)) return false;
  if (prev.isLinkHovered !== next.isLinkHovered) return false;
  return true;
});